import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-25">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm z-50" data-id="element-26" />
          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-white rounded-3xl shadow-2xl z-50 flex flex-col overflow-hidden" data-id="element-27">
            <div className="flex items-center justify-between p-6 border-b border-cream-dark" data-id="element-28">
              <h3 className="text-2xl font-serif text-burgundy-dark" data-id="element-29">
                {title}
              </h3>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-cream flex items-center justify-center text-charcoal-light hover:bg-rose-light hover:text-burgundy transition-colors" data-id="element-30">
                <XIcon className="w-5 h-5" data-id="element-31" />
              </button>
            </div>
            <div className="p-6 md:p-8 overflow-y-auto text-charcoal-light leading-relaxed space-y-4" data-id="element-32">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="py-10 px-4 md:px-8 bg-charcoal text-cream/60 text-center" data-id="element-33">
        <div className="max-w-4xl mx-auto" data-id="element-34">
          <div className="flex items-center justify-center gap-6 mb-6" data-id="element-35">
            <button onClick={() => setPrivacyOpen(true)} className="text-sm hover:text-gold transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-36">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-37">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-sm hover:text-gold transition-colors underline underline-offset-4 decoration-cream/20 hover:decoration-gold" data-id="element-38">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-xs text-cream/40" data-id="element-39">
            © {new Date().getFullYear()} Todos los derechos reservados. Este
            producto es digital y se entrega por correo electrónico.
          </p>
        </div>
      </footer>

      {/* Privacy Modal */}
      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" data-id="element-40">
        <p data-id="element-41">
          <strong data-id="element-42">Última actualización:</strong> Enero 2026
        </p>
        <p data-id="element-43">
          Respetamos tu privacidad y nos comprometemos a proteger tu información
          personal. Esta política describe cómo recopilamos, usamos y protegemos
          tus datos.
        </p>
        <p data-id="element-44">
          <strong data-id="element-45">Información que recopilamos:</strong>
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-46">
          <li data-id="element-47">
            Nombre y dirección de correo electrónico al realizar tu compra.
          </li>
          <li data-id="element-48">
            Información de pago procesada de forma segura a través de Shopify.
          </li>
          <li data-id="element-49">Datos de navegación anónimos para mejorar tu experiencia.</li>
        </ul>
        <p data-id="element-50">
          <strong data-id="element-51">Uso de la información:</strong>
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-52">
          <li data-id="element-53">Entregar el producto digital adquirido.</li>
          <li data-id="element-54">Enviar comunicaciones relacionadas con tu compra.</li>
          <li data-id="element-55">Mejorar nuestros productos y servicios.</li>
        </ul>
        <p data-id="element-56">
          <strong data-id="element-57">Protección de datos:</strong> Utilizamos cifrado SSL y
          procesadores de pago certificados PCI-DSS para proteger tu
          información.
        </p>
        <p data-id="element-58">
          <strong data-id="element-59">Tus derechos:</strong> Puedes solicitar la eliminación de tus
          datos en cualquier momento escribiéndonos a nuestro correo de soporte.
        </p>
        <p data-id="element-60">
          No compartimos, vendemos ni alquilamos tu información personal a
          terceros.
        </p>
      </LegalModal>

      {/* Terms Modal */}
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" data-id="element-61">
        <p data-id="element-62">
          <strong data-id="element-63">Última actualización:</strong> Enero 2026
        </p>
        <p data-id="element-64">
          Al adquirir este producto digital, aceptas los siguientes términos y
          condiciones:
        </p>
        <p data-id="element-65">
          <strong data-id="element-66">Producto:</strong>
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-67">
          <li data-id="element-68">
            Este es un producto digital (guía en formato PDF) que se entrega por
            correo electrónico inmediatamente después de la compra.
          </li>
          <li data-id="element-69">
            El contenido es para uso personal y no puede ser redistribuido,
            copiado ni revendido.
          </li>
        </ul>
        <p data-id="element-70">
          <strong data-id="element-71">Garantía de devolución:</strong>
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-72">
          <li data-id="element-73">
            Ofrecemos una garantía de devolución de 7 días desde la fecha de
            compra.
          </li>
          <li data-id="element-74">
            Para solicitar un reembolso, contacta a nuestro equipo de soporte
            con tu comprobante de compra.
          </li>
        </ul>
        <p data-id="element-75">
          <strong data-id="element-76">Limitación de responsabilidad:</strong>
        </p>
        <ul className="list-disc pl-6 space-y-2" data-id="element-77">
          <li data-id="element-78">
            Este producto ofrece herramientas de autoconocimiento y no sustituye
            la terapia profesional.
          </li>
          <li data-id="element-79">Los resultados pueden variar según cada persona y situación.</li>
        </ul>
        <p data-id="element-80">
          <strong data-id="element-81">Propiedad intelectual:</strong> Todo el contenido está
          protegido por derechos de autor. Queda prohibida su reproducción sin
          autorización.
        </p>
      </LegalModal>
    </>;
}